import { useEffect } from "react";
import { QueryClient, QueryClientProvider, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  Navigate,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/lib/i18n";
import {
  getContentBundle,
  getMediaBundle,
  getLocales,
  getFavicon,
  getPwaConfig,
  getMarketingSeo,
  type FaviconLink,
  type PwaConfig,
  type MarketingSeoData,
} from "@/lib/content.functions";
import { getPageSeo, type PageSeo } from "@/lib/seo.functions";
import { seoToHead } from "@/lib/seo-head";
import { ThemeInjector, themeTokensQuery } from "@/components/ThemeInjector";
import { CookieBanner } from "@/components/CookieBanner";
import { InstallPwaButton } from "@/components/InstallPwaButton";

const contentBundleQuery = queryOptions({
  queryKey: ["cms", "content"],
  queryFn: () => getContentBundle(),
  staleTime: 60_000,
  refetchInterval: 60_000,
});

const localesQuery = queryOptions({
  queryKey: ["cms", "locales"],
  queryFn: () => getLocales(),
  staleTime: 60_000,
});

const faviconQuery = queryOptions({
  queryKey: ["cms", "favicon"],
  queryFn: () => getFavicon(),
  staleTime: 60_000,
});

const pwaQuery = queryOptions({
  queryKey: ["cms", "pwa"],
  queryFn: () => getPwaConfig(),
  staleTime: 60_000,
});

const marketingSeoQuery = queryOptions({
  queryKey: ["cms", "marketing-seo"],
  queryFn: () => getMarketingSeo(),
  staleTime: 60_000,
});


// Fallback icons bundled with the site, used while no favicon is published
// in the backoffice (the favicon API returns an empty `links` array). They
// point at the proxy routes, which serve bundled fallbacks in that case.
const DEFAULT_ICON_LINKS = [
  { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
  { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
  { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
  { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
];

const mediaBundleQuery = queryOptions({
  queryKey: ["cms", "media"],
  queryFn: () => getMediaBundle(),
  staleTime: 60_000,
  refetchInterval: 60_000,
});




function NotFoundComponent() {
  return <Navigate to="/" />;
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_TITLE = "RR Lex Juristas — Defensa legal de primer nivel";
const DEFAULT_DESCRIPTION =
  "Despacho de abogadas en Sevilla y Camas. Defensa penal, familia, mercantil y mediación con trato humano y honorarios transparentes.";
const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/P68hIbarargqEZkliScQjLSc0D42/social-images/social-1781515521043-social-image.webp";
const DEFAULT_SITE_NAME = "RR Lex Juristas";


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: (ctx) => {
    // Cast: TS can't infer the root loader's return type inside head().
    const loaderData = ctx.loaderData as
      | { faviconLinks?: FaviconLink[]; pwa?: PwaConfig; marketing?: MarketingSeoData; pageSeo?: PageSeo | null }
      | undefined;
    const faviconLinks = loaderData?.faviconLinks ?? [];
    const pwaEnabled = Boolean(loaderData?.pwa?.enabled);
    const themeColor = loaderData?.pwa?.theme_color ?? "#000000";
    const appTitle = loaderData?.pwa?.short_name ?? loaderData?.pwa?.name ?? "RR Lex Juristas";

    const seo = loaderData?.marketing?.seo ?? null;
    const robotsIndex = loaderData?.marketing?.robotsIndex ?? true;

    const title = seo?.siteTitle?.trim() || DEFAULT_TITLE;
    const description = seo?.metaDescription?.trim() || DEFAULT_DESCRIPTION;
    const ogTitle = seo?.og?.title?.trim() || title;
    const ogDescription = seo?.og?.description?.trim() || description;
    const ogImage = seo?.og?.image?.trim() || DEFAULT_OG_IMAGE;
    const ogType = seo?.og?.type?.trim() || "website";
    const ogSiteName = seo?.og?.siteName?.trim() || DEFAULT_SITE_NAME;
    const twitterCard = seo?.twitter?.card || "summary";
    const twitterTitle = seo?.twitter?.title?.trim() || ogTitle;
    const twitterDescription = seo?.twitter?.description?.trim() || ogDescription;
    const twitterImage = seo?.twitter?.image?.trim() || ogImage;

    const meta: Array<Record<string, string>> = [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "2B2QXoQRnjIEH6epX46TdEjxa2aPuWVGzMrgje93430" },
      { title },
      { name: "description", content: description },
      { name: "author", content: ogSiteName },
      { name: "robots", content: robotsIndex ? "index,follow" : "noindex,nofollow" },
      { name: "theme-color", content: themeColor },
      { property: "og:title", content: ogTitle },
      { property: "og:description", content: ogDescription },
      { property: "og:site_name", content: ogSiteName },
      { property: "og:type", content: ogType },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: twitterCard },
      { name: "twitter:title", content: twitterTitle },
      { name: "twitter:description", content: twitterDescription },
      { name: "twitter:image", content: twitterImage },
    ];

    // Only advertise installability when the backoffice has PWA enabled —
    // otherwise Chrome would still offer "Install app" because of the
    // standalone metas.
    if (pwaEnabled) {
      meta.push(
        { name: "apple-mobile-web-app-capable", content: "yes" },
        { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
        { name: "apple-mobile-web-app-title", content: appTitle },
        { name: "mobile-web-app-capable", content: "yes" },
      );
    }

    const links: Array<Record<string, unknown>> = [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Preload Google Fonts CSS so the browser fetches it early without
      // render-blocking. The actual <link rel="stylesheet"> is attached
      // client-side from RootComponent to avoid blocking first paint.
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap",
      },
      { rel: "stylesheet", href: appCss },
      ...(faviconLinks.length
        ? faviconLinks.map((l) => ({
            rel: l.rel,
            href: l.href,
            ...(l.sizes ? { sizes: l.sizes } : {}),
            ...(l.type ? { type: l.type } : {}),
          }))
        : DEFAULT_ICON_LINKS),
    ];
    if (pwaEnabled) {
      links.push({ rel: "manifest", href: "/site.webmanifest" });
    }

    // Page-level SEO from the backoffice (single source of truth). It
    // overrides the site defaults above; leaf routes drop their own SEO tags
    // when it is present (see withSeo).
    const pageSeo = loaderData?.pageSeo;
    if (pageSeo) {
      const p = seoToHead(pageSeo);
      const keys = (m: Record<string, string>) => m.title !== undefined ? "title" : m.name ?? m.property;
      const overridden = new Set(p.meta.map(keys));
      return {
        meta: [...meta.filter((m) => !overridden.has(keys(m))), ...p.meta],
        links: [...links, ...p.links] as never,
        scripts: p.scripts,
      };
    }
    return { meta, links: links as never };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
  loader: async ({ context, location }) => {
    let locale: "es" | "en" | "pt" = "es";
    if (typeof window !== "undefined") {
      const l = window.localStorage.getItem("rrlex.lang");
      if (l === "en" || l === "pt") locale = l;
    }
    const [, , , , favicon, pwa, marketing, seoRes] = await Promise.all([
      context.queryClient.ensureQueryData(contentBundleQuery),
      context.queryClient.ensureQueryData(mediaBundleQuery),
      context.queryClient.ensureQueryData(themeTokensQuery),
      context.queryClient.ensureQueryData(localesQuery),
      context.queryClient.ensureQueryData(faviconQuery),
      context.queryClient.ensureQueryData(pwaQuery),
      context.queryClient.ensureQueryData(marketingSeoQuery),
      getPageSeo({ data: { path: location.pathname, locale } }).catch(() => ({ seo: null })),
    ]);
    return { faviconLinks: favicon.links, pwa, marketing, pageSeo: seoRes.seo };
  },
});


function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { data: content } = useSuspenseQuery(contentBundleQuery, queryClient);
  const { data: media } = useSuspenseQuery(mediaBundleQuery, queryClient);
  const { data: localesData } = useSuspenseQuery(localesQuery, queryClient);
  const { data: pwa } = useSuspenseQuery(pwaQuery, queryClient);

  // Lazily attach the Google Fonts stylesheet client-side so it doesn't
  // render-block the initial paint. The CSS is preloaded via head().links.
  useEffect(() => {
    const href =
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap";
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }, []);


  return (
    <QueryClientProvider client={queryClient}>
      <ThemeInjector />
      <LanguageProvider content={content.blocks} mediaBundle={media.assets} availableLocales={localesData.locales}>
        <Outlet />
        <Toaster />
        <CookieBanner />
        <InstallPwaButton enabled={pwa.enabled} label={pwa.install_button_label} />
      </LanguageProvider>
    </QueryClientProvider>
  );
}

